import isEmpty from 'lodash/isEmpty';
import { JSX, useContext } from 'react';
import { SearchFilterContentTypes } from 'search-gateway-client/components';

import { NavSearchContext } from '../../context/NavSearchContext';
import { RelatedContentTypeAndKeywords } from '../../types/shared';
import { ContentCollection, searchFilterContentTypeToContentClass } from '../NavSearchElements/NavSearchTypes';
import { SearchBarVariant } from '../NavSearchElements/searchUtils';
import AdobePluginSuggestion, { AdobePluginSuggestionProps } from './AdobePluginSuggestion';
import AutoSuggestDropdown, { AutoSuggestDropdownProps } from './AutoSuggestDropdown';
import CollectionsSuggestion, { CollectionsSuggestionProps } from './CollectionsSuggestion';
import KeywordsSuggestion, { KeywordsSuggestionProps } from './KeywordsSuggestion';
import RelatedContentSuggestion, { RelatedContentSuggestionProps } from './RelatedContentSuggestion';
import WithinAssetSuggestion, { WithinAssetSuggestionProps } from './WithinAssetSuggestion';
import { suggestionTypesKey } from './constants';
import { ComputedSuggestion } from './utils';

export type PopulatedSuggestions = {
    keywords?: string[];
    relatedCollections?: ContentCollection[];
    relatedContentTypesAndKeywords?: RelatedContentTypeAndKeywords[];
};

export interface PopulatedSearchDropdownProps {
    showSuggestions?: boolean;
    autoSuggestDropdownProps?: Omit<AutoSuggestDropdownProps, 'show'>;
    keywordsSuggestionProps?: Omit<KeywordsSuggestionProps, 'keywords'>;
    collectionsSuggestionProps?: Omit<CollectionsSuggestionProps, 'collections'>;
    withinAssetSuggestionProps?: Omit<WithinAssetSuggestionProps, 'contentClass' | 'withinAssetContentTypeSuggestions'>;
    relatedContentSuggestionProps?: Omit<RelatedContentSuggestionProps, 'relatedContentTypeSuggestions'>;
    adobeSuggestionProps?: AdobePluginSuggestionProps;
    suggestions?: ComputedSuggestion;
    contentType: SearchFilterContentTypes;
    useNewSearchUI?: boolean;
    useWarpSearchUI?: boolean;
}

const containersearchBarVariantStyle = {
    [SearchBarVariant.ProminentSearch]:
        'flex flex-col lg:flex-row gap-6 lg:gap-0 h-[36rem] lg:h-auto overflow-y-scroll lg:overflow-visible',
};

const leftPaneSearchBarVariantStyle = {
    [SearchBarVariant.ProminentSearch]: 'flex-grow flex-shrink-0 flex flex-col mr-8 lg:w-[9.5rem]',
};

const rightPaneSearchBarVariantStyle = {
    [SearchBarVariant.ProminentSearch]: 'flex flex-col flex-grow gap-y-5',
};

const PopulatedSearchDropdown = ({
    showSuggestions = false,
    autoSuggestDropdownProps = {},
    keywordsSuggestionProps = {},
    collectionsSuggestionProps = {},
    withinAssetSuggestionProps = {},
    relatedContentSuggestionProps = {},
    adobeSuggestionProps,
    suggestions = {
        keywords: [],
        relatedCollections: [],
        withinAssetContentTypeSuggestions: [],
        relatedContentTypeSuggestion: [],
        suggestedPages: [],
        adobePluginLink: [],
    },
    contentType,
    useNewSearchUI,
    useWarpSearchUI = false,
}: PopulatedSearchDropdownProps): JSX.Element => {
    const context = useContext(NavSearchContext);
    const { searchBarVariant } = context;

    return (
        <AutoSuggestDropdown
            show={showSuggestions}
            className={`flex flex-col ${
                !isEmpty(suggestions?.keywords) &&
                isEmpty(suggestions?.relatedContentTypeSuggestion) &&
                isEmpty(suggestions?.withinAssetContentTypeSuggestions) &&
                isEmpty(suggestions?.relatedCollections)
                    ? 'w-full'
                    : ''
            }`}
            {...autoSuggestDropdownProps}
            useNewSearchUI={useNewSearchUI}
            useWarpSearchUI={useWarpSearchUI}
        >
            <div
                className={
                    containersearchBarVariantStyle[searchBarVariant as keyof typeof containersearchBarVariantStyle] ??
                    'flex flex-row gap-x-0.5'
                }
            >
                <div
                    className={
                        leftPaneSearchBarVariantStyle[searchBarVariant as keyof typeof leftPaneSearchBarVariantStyle] ??
                        (useWarpSearchUI
                            ? 'w-[calc(32vw-20px)] lg:w-[14.375rem] grow shrink flex flex-col gap-y-2'
                            : 'w-[32vw] lg:w-[15.625rem] grow shrink flex flex-col gap-y-2')
                    }
                >
                    <KeywordsSuggestion
                        keywords={suggestions.keywords}
                        {...keywordsSuggestionProps}
                        useNewSearchUI={useNewSearchUI}
                    />
                </div>
                <div
                    className={
                        useWarpSearchUI
                            ? rightPaneSearchBarVariantStyle[
                                  searchBarVariant as keyof typeof rightPaneSearchBarVariantStyle
                              ] ?? 'flex flex-col grow gap-y-5 pr-4'
                            : rightPaneSearchBarVariantStyle[
                                  searchBarVariant as keyof typeof rightPaneSearchBarVariantStyle
                              ] ?? 'flex flex-col grow py-5 gap-y-5 pr-8'
                    }
                >
                    <WithinAssetSuggestion
                        {...withinAssetSuggestionProps}
                        contentClass={searchFilterContentTypeToContentClass[contentType]}
                        withinAssetContentTypeSuggestions={suggestions.withinAssetContentTypeSuggestions}
                        useNewSearchUI={useNewSearchUI}
                        useWarpSearchUI={useWarpSearchUI}
                    />
                    <RelatedContentSuggestion
                        {...relatedContentSuggestionProps}
                        relatedContentTypeSuggestions={suggestions.relatedContentTypeSuggestion}
                        useNewSearchUI={useNewSearchUI}
                        useWarpSearchUI={useWarpSearchUI}
                    />
                    <CollectionsSuggestion
                        collections={suggestions.relatedCollections}
                        title="Related Collections"
                        getTelemetryString={(routeUri) => `typeahead.collection.${routeUri}.click`}
                        idLabel={`${suggestionTypesKey.RELATED_COLLECTION}`}
                        {...collectionsSuggestionProps}
                        useNewSearchUI={useNewSearchUI}
                    />
                </div>
            </div>
            <AdobePluginSuggestion
                searchTerm={keywordsSuggestionProps.searchTerm || ''}
                variant={adobeSuggestionProps?.variant || 0}
                show={!!adobeSuggestionProps?.show}
                id={adobeSuggestionProps?.id}
                selected={adobeSuggestionProps?.selected}
                useNewSearchUI={useNewSearchUI}
            />
        </AutoSuggestDropdown>
    );
};

export default PopulatedSearchDropdown;
